import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, ArrowRight, AlertCircle } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { authService } from '../../services/authService';
import { getApiUrl } from '../../config/env';
import Button from '../../components/UI/Button';
import {
  AuthCard,
  AuthCardHeader,
  AuthField,
  AuthInputWrapper,
  authInputWithIconClass,
  AuthSuccessState,
  AuthPageFrame,
} from '../../components/Auth';

interface ForgotPasswordFormData {
  email: string;
}

const friendlyRequestError = (err: unknown): string => {
  const message = err instanceof Error ? err.message : 'Something went wrong';
  if (/404|not found|status code 404/i.test(message)) {
    return 'Password reset is not set up on the server yet. Please contact support or try again later.';
  }
  if (/Network Error|Failed to fetch|ECONNREFUSED|ERR_NETWORK|fetch/i.test(message)) {
    const apiUrl = getApiUrl();
    return `Cannot reach the API at ${apiUrl}. Start your backend or set REACT_APP_API_URL in .env.`;
  }
  return message;
};

const ForgotPassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>();

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setIsLoading(true);
      setFormError(null);
      await authService.requestPasswordReset(data.email.trim());
      setSubmitted(true);
    } catch (err) {
      setFormError(friendlyRequestError(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageFrame>
    <AuthCard>
        <AuthCardHeader
          title="Reset password"
          subtitle="We'll email you a secure link to choose a new password"
        />

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthSuccessState
                title="Check your inbox"
                description="We've sent a secure password reset link to your email. If an account exists for that address, follow the link to set a new password."
              >
                <Link to="/login">
                  <Button type="button" variant="primary" size="lg" className="w-full rounded-xl">
                    Back to sign in
                  </Button>
                </Link>
              </AuthSuccessState>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-4"
              noValidate
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <AuthField label="Email address" htmlFor="forgot-email" error={errors.email?.message}>
                <AuthInputWrapper hasError={!!errors.email}>
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Mail className="h-4 w-4 text-gray-400 transition-colors group-focus-within:text-blue-500" aria-hidden />
                  </div>
                  <input
                    id="forgot-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className={authInputWithIconClass(!!errors.email)}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    })}
                  />
                </AuthInputWrapper>
              </AuthField>

              {formError ? (
                <motion.div
                  role="alert"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2.5"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
                  <p className="text-sm text-red-500">{formError}</p>
                </motion.div>
              ) : null}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isLoading}
                className="w-full rounded-xl"
              >
                Send reset link
                {!isLoading ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden /> : null}
              </Button>
            </motion.form>
          )}
        </AnimatePresence>

        {!submitted ? (
          <p className="mt-6 text-center text-sm text-gray-600">
            Remember your password?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </p>
        ) : null}

      </AuthCard>
    </AuthPageFrame>
  );
};

export default ForgotPassword;
